import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Alert } from '@televet/kibble-ui/Alert';
import { Button } from '@televet/kibble-ui/Button';
import { Card } from '@televet/kibble-ui/Card';
import { Select } from '@televet/kibble-ui/Select';
import { Tag, type TagVariant } from '@televet/kibble-ui/Tag';
import { Text } from '@televet/kibble-ui/Text';
import { TextInput } from '@televet/kibble-ui/TextInput';
import { Flex, Table } from '@televet/kibble-ui/chakra';
import { trpc } from '@/lib/trpc';
import { useQuery } from '@tanstack/react-query';
import { type ComponentProps, useCallback, useMemo, useState } from 'react';
import { PageContainer } from '@/components/PageContainer';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { EChartRenderer } from '@astro/shared-ui';
import { FilterPills, type FilterPill } from '@/features/listing-filters/FilterPills';
import { useListingFilters } from '@/features/listing-filters/useListingFilters';
import {
  REPORTING_DATE_PRESETS,
  REPORTING_DEFAULT_DAYS,
  REPORTING_DEFAULT_GRANULARITY,
  activeDatePreset,
  daysBefore,
  formatDate,
  reportingFiltersSearchSchema,
  type ReportingFilterValues,
} from './-lib/reportingFilters';

export const Route = createFileRoute('/_authenticated/emitters/reporting')({
  component: ReportingDashboard,
  validateSearch: reportingFiltersSearchSchema,
});

type ChartOptions = ComponentProps<typeof EChartRenderer>['options'];

type LeaderboardSortKey = 'executionCount' | 'totalResults' | 'failureCount';

export function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

export function formatDuration(ms: number): string {
  if (ms >= 60_000) return `${(ms / 60_000).toFixed(1)}m`;
  if (ms >= 1_000) return `${(ms / 1_000).toFixed(1)}s`;
  return `${ms}ms`;
}

/** Validates date range. Returns error message or null. */
export function validateDateRange(startDate: string, endDate: string): string | null {
  if (!startDate || !endDate) return 'Start and end dates are required';
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return 'Invalid date';
  if (start > end) return 'Start date must be before end date';
  const diffDays = (end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000);
  if (diffDays > 365) return 'Date range cannot exceed 365 days';
  return null;
}

function ErrorBanner({ message }: { message: string }) {
  return <Alert status="error" hideCloseButton description={message} />;
}

function EmptyState({ message }: { message: string }) {
  return (
    <Flex h="180px" align="center" justify="center">
      <Text fontSize="sm" variant="subtle">
        {message}
      </Text>
    </Flex>
  );
}

const successRateVariant = (rate: number): TagVariant =>
  rate >= 95 ? 'success' : rate >= 80 ? 'warning' : 'error';

const DATE_FIELD_STYLE = { maxWidth: '150px' } as const;
const FILTER_SELECT_STYLE = { width: '180px' } as const;
const TEMPLATE_FILTER_STYLE = { width: '280px' } as const;
const TEMPLATE_FILTER_CONTENT_PROPS = { minW: 'var(--reference-width)', maxW: '640px' } as const;

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Failed', label: 'Failed' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Running', label: 'Running' },
  { value: 'Cancelled', label: 'Cancelled' },
];

const MODE_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'Full', label: 'Full' },
  { value: 'PersistOnly', label: 'Persist Only' },
  { value: 'DryRun', label: 'Dry Run' },
];

const STATUS_COLORS: Record<string, string> = {
  Completed: '#48bb78',
  Failed: '#f56565',
  Pending: '#a0aec0',
  Running: '#4299e1',
  Cancelled: '#ecc94b',
};

function ReportingDashboard() {
  const navigate = useNavigate();
  const routeNavigate = Route.useNavigate();
  const search = Route.useSearch();
  const [leaderboardSort, setLeaderboardSort] = useState<LeaderboardSortKey>('executionCount');

  const patchFilters = useCallback(
    (next: Partial<ReportingFilterValues>) => {
      routeNavigate({ search: (prev) => ({ ...prev, ...next }) });
    },
    [routeNavigate]
  );

  useListingFilters({
    page: 'emitter-reporting',
    schema: reportingFiltersSearchSchema,
    filters: search,
    onRehydrate: (restored) => routeNavigate({ search: restored }),
  });

  // The dashboard opens on the last 7 days by day. Those defaults are applied
  // here rather than written into the URL, so a URL with no params stays bare
  // and saved filters can be restored on the next visit.
  const now = new Date();
  const startDate = search.startDate ?? daysBefore(now, REPORTING_DEFAULT_DAYS);
  const endDate = search.endDate ?? formatDate(now);
  const activePreset = activeDatePreset(startDate, endDate, now);
  const granularity = search.granularity ?? REPORTING_DEFAULT_GRANULARITY;
  const templateId = search.templateId ?? '';
  const statusFilter = search.status ?? '';
  const modeFilter = search.executionMode ?? '';

  const dateError = validateDateRange(startDate, endDate);
  const filtersValid = dateError === null;

  const filters = {
    startDate,
    endDate,
    granularity,
    ...(templateId ? { templateId } : {}),
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(modeFilter ? { executionMode: modeFilter } : {}),
  };

  // Data queries - all fire in parallel, disabled when filters are invalid
  const { data: templates } = useQuery(trpc.emitter.listTemplates.queryOptions());
  const {
    data: summary,
    isFetching: summaryLoading,
    error: summaryError,
  } = useQuery({
    ...trpc.emitter.getReportingSummary.queryOptions(filters),
    enabled: filtersValid,
  });
  const { data: timeSeries, error: timeSeriesError } = useQuery({
    ...trpc.emitter.getExecutionTimeSeries.queryOptions(filters),
    enabled: filtersValid,
  });
  const { data: templatePerf, error: templatePerfError } = useQuery({
    ...trpc.emitter.getTemplatePerformance.queryOptions(filters),
    enabled: filtersValid,
  });
  const { data: resultAnalytics, error: resultAnalyticsError } = useQuery({
    ...trpc.emitter.getResultAnalytics.queryOptions(filters),
    enabled: filtersValid,
  });
  const { data: leaderboard, error: leaderboardError } = useQuery({
    ...trpc.emitter.getInstanceLeaderboard.queryOptions(filters),
    enabled: filtersValid,
  });

  const queryError =
    summaryError ??
    timeSeriesError ??
    templatePerfError ??
    resultAnalyticsError ??
    leaderboardError;

  const sortedLeaderboard = useMemo(() => {
    if (!leaderboard) return [];
    return [...leaderboard].sort((a, b) => b[leaderboardSort] - a[leaderboardSort]);
  }, [leaderboard, leaderboardSort]);

  const handleStartDateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      patchFilters({ startDate: e.target.value || undefined }),
    []
  );
  const handleEndDateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      patchFilters({ endDate: e.target.value || undefined }),
    []
  );

  // --- Chart Options ---

  const executionVolumeOptions = useMemo((): ChartOptions | null => {
    if (!timeSeries?.length) return null;
    const buckets = timeSeries.map((t) =>
      granularity === 'hour'
        ? new Date(t.bucket).toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
          })
        : new Date(t.bucket).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
          })
    );
    return {
      tooltip: { trigger: 'axis' },
      legend: {
        data: ['Completed', 'Failed', 'Pending', 'Cancelled'],
        bottom: 0,
      },
      grid: { left: '3%', right: '4%', bottom: '15%', containLabel: true },
      xAxis: { type: 'category', data: buckets },
      yAxis: { type: 'value' },
      series: [
        {
          name: 'Completed',
          type: 'bar',
          stack: 'total',
          data: timeSeries.map((t) => t.completed),
          itemStyle: { color: STATUS_COLORS.Completed },
        },
        {
          name: 'Failed',
          type: 'bar',
          stack: 'total',
          data: timeSeries.map((t) => t.failed),
          itemStyle: { color: STATUS_COLORS.Failed },
        },
        {
          name: 'Pending',
          type: 'bar',
          stack: 'total',
          data: timeSeries.map((t) => t.pending),
          itemStyle: { color: STATUS_COLORS.Pending },
        },
        {
          name: 'Cancelled',
          type: 'bar',
          stack: 'total',
          data: timeSeries.map((t) => t.cancelled),
          itemStyle: { color: STATUS_COLORS.Cancelled },
        },
      ],
    };
  }, [timeSeries, granularity]);

  const resultVolumeOptions = useMemo((): ChartOptions | null => {
    if (!timeSeries?.length) return null;
    const buckets = timeSeries.map((t) =>
      new Date(t.bucket).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      })
    );
    return {
      tooltip: { trigger: 'axis' },
      legend: { data: ['New (Emitted)', 'Deduped'], bottom: 0 },
      grid: { left: '3%', right: '4%', bottom: '15%', containLabel: true },
      xAxis: { type: 'category', data: buckets },
      yAxis: { type: 'value' },
      series: [
        {
          name: 'New (Emitted)',
          type: 'line',
          stack: 'results',
          areaStyle: { opacity: 0.4 },
          data: timeSeries.map((t) => t.newResults),
          itemStyle: { color: '#4299e1' },
        },
        {
          name: 'Deduped',
          type: 'line',
          stack: 'results',
          areaStyle: { opacity: 0.4 },
          data: timeSeries.map((t) => t.dedupedResults),
          itemStyle: { color: '#a0aec0' },
        },
      ],
    };
  }, [timeSeries]);

  const emissionFunnelOptions = useMemo((): ChartOptions | null => {
    if (!resultAnalytics) return null;
    const b = resultAnalytics.emissionBreakdown;
    if (b.total === 0) return null;
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
      xAxis: { type: 'value' },
      yAxis: {
        type: 'category',
        data: ['Total Results', 'Emitted', 'Delivered'],
        inverse: true,
      },
      series: [
        {
          type: 'bar',
          data: [
            { value: b.total, itemStyle: { color: '#a0aec0' } },
            { value: b.emitted, itemStyle: { color: '#4299e1' } },
            { value: b.delivered, itemStyle: { color: '#48bb78' } },
          ],
          label: {
            show: true,
            position: 'right',
            formatter: (params: any) => formatNumber(params.value),
          },
          barWidth: '60%',
        },
      ],
    };
  }, [resultAnalytics]);

  const entityDistributionOptions = useMemo((): ChartOptions | null => {
    if (!resultAnalytics?.entityTypeDistribution.length) return null;
    return {
      tooltip: { trigger: 'item' },
      series: [
        {
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: true,
          label: { show: true, formatter: '{b}: {c}' },
          data: resultAnalytics.entityTypeDistribution.map((e) => ({
            name: e.entityType,
            value: e.count,
          })),
        },
      ],
    };
  }, [resultAnalytics]);

  const durationTrendOptions = useMemo((): ChartOptions | null => {
    if (!timeSeries?.length) return null;
    const buckets = timeSeries.map((t) =>
      new Date(t.bucket).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      })
    );
    return {
      tooltip: { trigger: 'axis' },
      legend: { data: ['Avg Duration'], bottom: 0 },
      grid: { left: '3%', right: '4%', bottom: '15%', containLabel: true },
      xAxis: { type: 'category', data: buckets },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (v: number) => formatDuration(v),
        },
      },
      series: [
        {
          name: 'Avg Duration',
          type: 'line',
          data: timeSeries.map((t) => t.avgDurationMs),
          itemStyle: { color: '#805ad5' },
          smooth: true,
        },
      ],
    };
  }, [timeSeries]);

  const pills = useMemo<FilterPill[]>(() => {
    const all: FilterPill[] = [];
    if (search.startDate || search.endDate) {
      all.push({
        key: 'dates',
        label: `${startDate} to ${endDate}`,
        onRemove: () => patchFilters({ startDate: undefined, endDate: undefined }),
      });
    }
    if (search.templateId) {
      const name = templates?.find((template) => template.id === search.templateId)?.name;
      all.push({
        key: 'templateId',
        label: `Template: ${name ?? search.templateId.slice(0, 8)}`,
        onRemove: () => patchFilters({ templateId: undefined }),
      });
    }
    if (search.status) {
      all.push({
        key: 'status',
        label: `Status: ${search.status}`,
        onRemove: () => patchFilters({ status: undefined }),
      });
    }
    if (search.executionMode) {
      all.push({
        key: 'executionMode',
        label: `Mode: ${search.executionMode}`,
        onRemove: () => patchFilters({ executionMode: undefined }),
      });
    }
    if (search.granularity && search.granularity !== REPORTING_DEFAULT_GRANULARITY) {
      all.push({
        key: 'granularity',
        label: `By ${search.granularity}`,
        onRemove: () => patchFilters({ granularity: undefined }),
      });
    }
    return all;
  }, [search, startDate, endDate, templates, patchFilters]);

  const setPreset = (days: number) => {
    const presetNow = new Date();
    patchFilters({ startDate: daysBefore(presetNow, days), endDate: formatDate(presetNow) });
  };

  return (
    <PageContainer>
      <Flex direction="column" gap={3} width="100%">
        <Breadcrumbs items={[{ label: 'Reporting' }]} />

        <Flex justify="space-between" align="center">
          <Text fontSize="lg" fontWeight="semibold" letterSpacing="-0.01em">
            Reporting Dashboard
          </Text>
        </Flex>

        {/* Filters */}
        <Card size="sm" py={3} px={4}>
          <Flex gap={3} wrap="wrap" align="flex-end">
            <Flex direction="column" gap={1}>
              <Text fontSize="xs" variant="subtle" fontWeight="medium">
                Date Range
              </Text>
              <Flex align="center" gap={2}>
                <TextInput
                  type="date"
                  size="sm"
                  value={startDate}
                  onChange={handleStartDateChange}
                  formControlStyle={DATE_FIELD_STYLE}
                  invalid={!!dateError}
                  useInvalidText={false}
                />
                <Text fontSize="xs" variant="subtle">
                  to
                </Text>
                <TextInput
                  type="date"
                  size="sm"
                  value={endDate}
                  onChange={handleEndDateChange}
                  formControlStyle={DATE_FIELD_STYLE}
                  invalid={!!dateError}
                  useInvalidText={false}
                />
              </Flex>
            </Flex>

            <Flex gap={1}>
              {REPORTING_DATE_PRESETS.map((days) => (
                <Button
                  key={days}
                  size="xs"
                  variant={activePreset === days ? 'primary' : 'secondary'}
                  onClick={() => setPreset(days)}
                >
                  {days}d
                </Button>
              ))}
            </Flex>

            <Flex direction="column" gap={1}>
              <Text fontSize="xs" variant="subtle" fontWeight="medium">
                Template
              </Text>
              <Select
                size="sm"
                fullWidth={false}
                formControlStyle={TEMPLATE_FILTER_STYLE}
                positioning={{ sameWidth: false }}
                contentProps={TEMPLATE_FILTER_CONTENT_PROPS}
                options={[
                  { value: '', label: 'All Templates' },
                  ...(templates ?? []).map((t) => ({ value: t.id, label: t.name })),
                ]}
                value={[templateId]}
                onValueChange={(details) =>
                  patchFilters({ templateId: details.value[0] || undefined })
                }
              />
            </Flex>

            <Flex direction="column" gap={1}>
              <Text fontSize="xs" variant="subtle" fontWeight="medium">
                Status
              </Text>
              <Select
                size="sm"
                fullWidth={false}
                formControlStyle={FILTER_SELECT_STYLE}
                options={STATUS_FILTER_OPTIONS}
                value={[statusFilter]}
                onValueChange={(details) =>
                  patchFilters({
                    status: (details.value[0] || undefined) as ReportingFilterValues['status'],
                  })
                }
              />
            </Flex>

            <Flex direction="column" gap={1}>
              <Text fontSize="xs" variant="subtle" fontWeight="medium">
                Mode
              </Text>
              <Select
                size="sm"
                fullWidth={false}
                formControlStyle={FILTER_SELECT_STYLE}
                options={MODE_FILTER_OPTIONS}
                value={[modeFilter]}
                onValueChange={(details) =>
                  patchFilters({
                    executionMode: (details.value[0] ||
                      undefined) as ReportingFilterValues['executionMode'],
                  })
                }
              />
            </Flex>

            <Flex direction="column" gap={1}>
              <Text fontSize="xs" variant="subtle" fontWeight="medium">
                Granularity
              </Text>
              <Flex gap={1}>
                {(['hour', 'day', 'week'] as const).map((g) => (
                  <Button
                    key={g}
                    size="xs"
                    variant={granularity === g ? 'primary' : 'secondary'}
                    onClick={() => patchFilters({ granularity: g })}
                  >
                    {g.charAt(0).toUpperCase() + g.slice(1)}
                  </Button>
                ))}
              </Flex>
            </Flex>
          </Flex>
          <FilterPills pills={pills} onClearAll={() => routeNavigate({ search: {} })} />
        </Card>

        {dateError && <ErrorBanner message={dateError} />}
        {queryError && (
          <ErrorBanner message={`Failed to load reporting data: ${queryError.message}`} />
        )}

        {/* KPI Cards */}
        {summary && (
          <Flex gap={3} wrap="wrap">
            <Card flex={1} minW="200px" py={3} px={4}>
              <Text fontSize="xs" variant="subtle" fontWeight="medium">
                EXECUTIONS
              </Text>
              <Text fontSize="2xl" fontWeight="bold">
                {formatNumber(summary.totalExecutions)}
              </Text>
              <Flex gap={2} align="center" wrap="wrap">
                <Tag
                  size="sm"
                  variant={successRateVariant(summary.successRate)}
                  label={`${summary.successRate}% success`}
                />
                {summary.failedExecutions > 0 && (
                  <Text fontSize="xs" variant="danger">
                    {summary.failedExecutions} failed
                  </Text>
                )}
              </Flex>
            </Card>

            <Card flex={1} minW="200px" py={3} px={4}>
              <Text fontSize="xs" variant="subtle" fontWeight="medium">
                RESULTS
              </Text>
              <Text fontSize="2xl" fontWeight="bold">
                {formatNumber(summary.totalResults)}
              </Text>
              <Flex gap={2} align="center" wrap="wrap">
                <Tag size="sm" variant="info" label={`${summary.emissionRate}% emitted`} />
                <Text fontSize="xs" variant="subtle">
                  {formatNumber(summary.newResults)} new / {formatNumber(summary.dedupedResults)}{' '}
                  deduped
                </Text>
              </Flex>
            </Card>

            <Card flex={1} minW="200px" py={3} px={4}>
              <Text fontSize="xs" variant="subtle" fontWeight="medium">
                DURATION
              </Text>
              <Text fontSize="2xl" fontWeight="bold">
                {formatDuration(summary.avgDurationMs)}
              </Text>
              <Flex gap={2} align="center">
                <Text fontSize="xs" variant="subtle">
                  p50: {formatDuration(summary.p50DurationMs)}
                </Text>
                <Text fontSize="xs" variant="subtle">
                  p95: {formatDuration(summary.p95DurationMs)}
                </Text>
              </Flex>
            </Card>

            <Card flex={1} minW="200px" py={3} px={4}>
              <Text fontSize="xs" variant="subtle" fontWeight="medium">
                ACTIVE
              </Text>
              <Flex gap={3} align="baseline">
                <Flex direction="column">
                  <Text fontSize="2xl" fontWeight="bold">
                    {summary.activeInstances}
                  </Text>
                  <Text fontSize="xs" variant="subtle">
                    instances
                  </Text>
                </Flex>
                <Flex direction="column">
                  <Text fontSize="2xl" fontWeight="bold">
                    {summary.activeTemplates}
                  </Text>
                  <Text fontSize="xs" variant="subtle">
                    templates
                  </Text>
                </Flex>
              </Flex>
            </Card>
          </Flex>
        )}

        {summaryLoading && !summary && (
          <Text fontSize="sm" variant="subtle">
            Loading dashboard data...
          </Text>
        )}

        {!summaryLoading &&
          !summaryError &&
          filtersValid &&
          summary &&
          summary.totalExecutions === 0 && (
            <Card py={6}>
              <EmptyState message="No executions found for the selected date range and filters." />
            </Card>
          )}

        {/* Charts Row */}
        <Flex gap={3} wrap="wrap">
          <Card flex={1} minW="400px" py={3} px={4}>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              Execution Volume
            </Text>
            {executionVolumeOptions ? (
              <EChartRenderer options={executionVolumeOptions} height="280px" minHeight="240px" />
            ) : (
              <Flex h="280px" align="center" justify="center">
                <Text fontSize="sm" variant="subtle">
                  No execution data
                </Text>
              </Flex>
            )}
          </Card>

          <Card flex={1} minW="400px" py={3} px={4}>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              Result Volume
            </Text>
            {resultVolumeOptions ? (
              <EChartRenderer options={resultVolumeOptions} height="280px" minHeight="240px" />
            ) : (
              <Flex h="280px" align="center" justify="center">
                <Text fontSize="sm" variant="subtle">
                  No result data
                </Text>
              </Flex>
            )}
          </Card>
        </Flex>

        {/* Second Charts Row */}
        <Flex gap={3} wrap="wrap">
          <Card flex={1} minW="400px" py={3} px={4}>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              Emission Pipeline
            </Text>
            {emissionFunnelOptions ? (
              <EChartRenderer options={emissionFunnelOptions} height="180px" minHeight="150px" />
            ) : (
              <Flex h="180px" align="center" justify="center">
                <Text fontSize="sm" variant="subtle">
                  No result data
                </Text>
              </Flex>
            )}
          </Card>

          <Card flex={1} minW="400px" py={3} px={4}>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              Duration Trend
            </Text>
            {durationTrendOptions ? (
              <EChartRenderer options={durationTrendOptions} height="180px" minHeight="150px" />
            ) : (
              <Flex h="180px" align="center" justify="center">
                <Text fontSize="sm" variant="subtle">
                  No duration data
                </Text>
              </Flex>
            )}
          </Card>
        </Flex>

        {/* Entity Distribution */}
        {entityDistributionOptions && (
          <Card py={3} px={4}>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              Entity Type Distribution
            </Text>
            <EChartRenderer options={entityDistributionOptions} height="300px" minHeight="250px" />
          </Card>
        )}

        {/* Template Performance Table */}
        {templatePerf && templatePerf.length > 0 && (
          <Flex direction="column" gap={2}>
            <Text fontSize="sm" fontWeight="semibold">
              Template Performance
            </Text>
            <Table.Root size="sm" width="100%" variant="outline">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Template</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="center">Executions</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="center">Success Rate</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="center">Results</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="center">Avg Duration</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="center">Instances</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {templatePerf.map((t) => (
                  <Table.Row
                    key={t.templateId}
                    cursor="pointer"
                    _hover={{ bg: 'bg.muted' }}
                    onClick={() =>
                      navigate({
                        to: `/emitter-templates/${t.templateId}`,
                      })
                    }
                  >
                    <Table.Cell>
                      <Text fontSize="sm" fontWeight="medium">
                        {t.templateName}
                      </Text>
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      <Text fontSize="xs">{t.executionCount}</Text>
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      <Tag
                        size="sm"
                        variant={successRateVariant(t.successRate)}
                        label={`${t.successRate}%`}
                      />
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      <Text fontSize="xs" variant="subtle">
                        {formatNumber(t.newResults)} / {formatNumber(t.totalResults)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      <Text fontSize="xs" variant="subtle">
                        {formatDuration(t.avgDurationMs)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      <Text fontSize="xs" variant="subtle">
                        {t.instanceCount}
                      </Text>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Flex>
        )}

        {/* Instance Leaderboard */}
        {sortedLeaderboard.length > 0 && (
          <Flex direction="column" gap={2}>
            <Flex justify="space-between" align="center">
              <Text fontSize="sm" fontWeight="semibold">
                Instance Leaderboard
              </Text>
              <Flex gap={1} align="center">
                <Text fontSize="xs" variant="subtle">
                  Sort by:
                </Text>
                {(
                  [
                    ['executionCount', 'Executions'],
                    ['totalResults', 'Results'],
                    ['failureCount', 'Failures'],
                  ] as const
                ).map(([key, label]) => (
                  <Button
                    key={key}
                    size="xs"
                    variant={leaderboardSort === key ? 'primary' : 'secondary'}
                    onClick={() => setLeaderboardSort(key)}
                  >
                    {label}
                  </Button>
                ))}
              </Flex>
            </Flex>
            <Table.Root size="sm" width="100%" variant="outline">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Instance</Table.ColumnHeader>
                  <Table.ColumnHeader>Template</Table.ColumnHeader>
                  <Table.ColumnHeader>Owner</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="center">Executions</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="center">Failures</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="center">Results</Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="center">Avg Duration</Table.ColumnHeader>
                  <Table.ColumnHeader>Last Run</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {sortedLeaderboard.map((inst) => (
                  <Table.Row
                    key={inst.instanceId}
                    cursor="pointer"
                    _hover={{ bg: 'bg.muted' }}
                    onClick={() =>
                      navigate({
                        to: `/emitters/${inst.instanceId}`,
                      })
                    }
                  >
                    <Table.Cell>
                      <Text fontSize="sm" fontWeight="medium">
                        {inst.instanceName ?? inst.instanceId.slice(0, 8)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="xs" variant="subtle">
                        {inst.templateName}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="xs" variant="subtle">
                        {inst.ownerName ?? `${inst.ownerType}: ${inst.ownerId}`}
                      </Text>
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      <Text fontSize="xs">{inst.executionCount}</Text>
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      {inst.failureCount > 0 ? (
                        <Tag size="sm" variant="error" label={String(inst.failureCount)} />
                      ) : (
                        <Text fontSize="xs" variant="subtle">
                          0
                        </Text>
                      )}
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      <Text fontSize="xs" variant="subtle">
                        {formatNumber(inst.newResults)} / {formatNumber(inst.totalResults)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      <Text fontSize="xs" variant="subtle">
                        {formatDuration(inst.avgDurationMs)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="xs" variant="subtle">
                        {inst.lastExecutionAt
                          ? new Date(inst.lastExecutionAt).toLocaleString()
                          : '-'}
                      </Text>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Flex>
        )}
      </Flex>
    </PageContainer>
  );
}
